import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { DataTable } from '../components/DataTable.tsx';
import { ItemLabel } from '../components/ItemLabel.tsx';
import { Card, Empty, Notice, Spinner } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/auth.tsx';
import { batchPrice, money, number, percent, unitMoney } from '../lib/format.ts';

type Result = Awaited<ReturnType<typeof api.market.whereToBuy>>['results'][number];

/**
 * Where to buy an item.
 *
 * The original brief was explicit that this platform is seller-facing and
 * should not become a bargain-finder. It is here because the same view answers
 * a seller's question too - "what does a customer see when they look for what I
 * sell" - and because the underlying data is public either way.
 */
export function BuyersPage() {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [results, setResults] = useState<Result[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const search = async (event: FormEvent) => {
    event.preventDefault();
    if (query.trim().length < 2) return;
    setBusy(true);
    setSubmitted(query.trim());
    try {
      const response = await api.market.whereToBuy(query.trim());
      setResults(response.results);
      setNote(response.note);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Where to buy</h1>
          <p>
            Find shops selling an item, cheapest first. Only signs with stock are shown - the
            cheapest listing on the server is very often an empty chest.
          </p>
        </div>
      </header>

      <Card>
        <form onSubmit={search} className="row" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0, minWidth: 260 }}>
            <label htmlFor="q">Item</label>
            <input
              id="q"
              value={query}
              placeholder="diamond, oak log, enchanted book..."
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="primary" disabled={busy || query.trim().length < 2}>
            {busy ? 'Searching...' : 'Search'}
          </button>
        </form>
      </Card>

      {busy && <Spinner />}
      {note && !busy && (
        <div style={{ marginTop: 16 }}>
          <Notice>{note}</Notice>
        </div>
      )}

      {results && results.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {results.map((result) => (
            <div key={result.itemKey}>
              <WorthCard itemKey={result.itemKey} />
              <ItemOffers result={result} />
            </div>
          ))}
        </div>
      )}

      {results && results.length === 0 && !note && (
        <Empty title={`Nothing in stock matches "${submitted}"`} />
      )}
    </>
  );
}

/**
 * What an item is worth, for anyone - not only people who already sell it.
 *
 * The price band was previously computed only for a subscriber's own shop, so
 * the question "what would this go for if I stocked it" had nowhere to be
 * asked. It is the question anyone deciding what to sell next actually has.
 */
function WorthCard({ itemKey }: { itemKey: string }) {
  const price = useAsync(() => api.insights.priceFor(itemKey), [itemKey]);

  if (price.loading || !price.data?.band) return null;
  const band = price.data.band;

  // Where the figure came from, in descending order of how much it deserves
  // to be trusted.
  const basis =
    band.tradeCount > 0
      ? `${number(band.tradeCount)} completed trades recorded by the server`
      : band.clearingUnits > 0
        ? `${number(band.clearingUnits)} units watched changing hands`
        : `${number(band.sampleSize)} shops asking - no completed sale seen`;

  return (
    <Card title={`What ${itemKey} is worth`} subtitle={basis}>
      <div className="price-band" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
        <span className="head">Low</span>
        <span className="head">Average</span>
        <span className="head">High</span>
        <span className="head rec">Suggested</span>
        <span>{unitMoney(band.low)}</span>
        <span>{unitMoney(band.average)}</span>
        <span>{unitMoney(band.high)}</span>
        <span className="rec">{unitMoney(band.recommended)}</span>
        <span className="price-band-foot">{band.recommendedReason}</span>
      </div>
    </Card>
  );
}

function ItemOffers({ result }: { result: Result }) {
  // Which chestshops this viewer has reported empty. A report belongs to a
  // shop's chest, never to the item - the item plainly exists, that is why it
  // is on screen.
  const [reported, setReported] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.insights
      .flagsFor(result.itemKey)
      .then((existing) => {
        if (!cancelled) setReported(new Set(existing.shopIds));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [result.itemKey]);

  /**
   * Report one chestshop as empty, or withdraw that report.
   *
   * This belongs to the buyer: the market data is a periodic snapshot, so the
   * chest we sent you to may have been emptied hours ago and only the person
   * who walked there knows. Reporting raises a paid check job, so one report
   * never hides a live shop on its own.
   */
  const toggle = async (shopId: number) => {
    setBusy(shopId);
    setNote(null);
    try {
      if (reported.has(shopId)) {
        await api.insights.clearFlag(result.itemKey, shopId);
        setReported((current) => {
          const next = new Set(current);
          next.delete(shopId);
          return next;
        });
        setNote('Report withdrawn.');
      } else {
        const outcome = await api.insights.flagItem(result.itemKey, shopId);
        setReported((current) => new Set(current).add(shopId));
        setNote(outcome.note);
      }
    } catch (error) {
      setNote(error instanceof Error ? error.message : 'Could not report that.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card
      title={
        <Link to={`/item/${encodeURIComponent(result.itemKey)}`} className="item-link">
          <ItemLabel
            itemKey={result.itemKey}
            material={result.material}
            displayName={result.itemName}
            size={26}
          />
        </Link>
      }
      subtitle={
        <>
          Cheapest {unitMoney(result.bestUnitPrice)} per unit
          {result.marketAvg != null && ` - server average ${unitMoney(result.marketAvg)}`} -{' '}
          {number(result.shopsInStock)} shop{result.shopsInStock === 1 ? '' : 's'} in stock,{' '}
          {number(result.totalStock)} units available
        </>
      }
    >
      {note && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="info">{note}</Notice>
        </div>
      )}
      {result.shopsInStock > result.offers.length && (
        <div className="row spread" style={{ marginBottom: 12 }}>
          <span className="small muted">
            Showing the {result.offers.length} cheapest of {number(result.shopsInStock)} shops.
          </span>
          <Link to={`/item/${encodeURIComponent(result.itemKey)}`}>
            <button className="primary">See all {number(result.shopsInStock)} shops</button>
          </Link>
        </div>
      )}
      <DataTable
        rows={result.offers}
        rowKey={(offer) => String(offer.shopId)}
        initialSort={{ key: 'unitPrice', dir: 'asc' }}
        searchPlaceholder="Search owner or world"
        columns={[
          {
            key: 'owner',
            header: 'Shop',
            searchText: (offer) => offer.ownerName ?? '',
            sortValue: (offer) => offer.ownerName,
            render: (offer) => (
              <>
                {offer.ownerName ?? <span className="muted">unnamed</span>}
                {offer.adminShop && <span className="small muted"> (admin shop)</span>}
              </>
            ),
          },
          {
            key: 'sign',
            header: 'Sign price',
            align: 'num',
            sortValue: (offer) => offer.signPrice,
            render: (offer) => batchPrice(offer.signPrice, offer.batchQty) ?? '-',
          },
          {
            key: 'unitPrice',
            header: 'Per unit',
            align: 'num',
            sortValue: (offer) => offer.unitPrice,
            render: (offer) => <strong>{unitMoney(offer.unitPrice)}</strong>,
          },
          {
            key: 'saving',
            header: 'vs average',
            align: 'num',
            sortValue: (offer) => offer.savingVsAvgPct,
            render: (offer) =>
              offer.savingVsAvgPct == null ? (
                <span className="muted">-</span>
              ) : (
                <span className={offer.savingVsAvgPct < 0 ? 'delta-up' : 'delta-down'}>
                  {percent(offer.savingVsAvgPct)}
                </span>
              ),
          },
          {
            key: 'stock',
            header: 'Stock',
            align: 'num',
            sortValue: (offer) => offer.currentStock,
            render: (offer) => number(offer.currentStock ?? 0),
          },
          {
            key: 'cost',
            header: 'Cost for all',
            align: 'num',
            sortValue: (offer) => offer.unitPrice * (offer.currentStock ?? 0),
            render: (offer) => money(offer.unitPrice * (offer.currentStock ?? 0)),
          },
          {
            key: 'where',
            header: 'Location',
            searchText: (offer) => `${offer.world ?? ''} ${offer.coords ?? ''}`,
            sortValue: (offer) => offer.world,
            render: (offer) => (
              <span className="muted small">
                {offer.world ?? '-'}
                {offer.coords ? <div>{offer.coords}</div> : null}
              </span>
            ),
          },
          {
            key: 'report',
            header: '',
            width: '104px',
            render: (offer) => (
              <button
                className={`ghost ${reported.has(offer.shopId) ? 'flagged' : ''}`}
                style={{ padding: '2px 8px', fontSize: 11 }}
                disabled={busy != null}
                title={
                  reported.has(offer.shopId)
                    ? 'You reported this chest as empty - click to withdraw'
                    : 'Walked there and the chest was empty or gone? Report this shop.'
                }
                onClick={() => toggle(offer.shopId)}
              >
                {busy === offer.shopId
                  ? '...'
                  : reported.has(offer.shopId)
                    ? 'Reported'
                    : 'Report empty'}
              </button>
            ),
          },
        ]}
      />
    </Card>
  );
}
